import { useState, useEffect } from 'react'
import MetricsHeaderBackground from './MetricsHeaderBackground'
import Refresh from '@material-ui/icons/Refresh'
import ListenFeedbackIcon from './ListenFeedbackIcon'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import effects from 'app/effects/effects'
import z from 'zod'
import { timeAgo } from 'commons/date'
import NewMetricsRecipientList from './NewMetricsRecipientList'
import { CircularProgress } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'

export default function NewMetrics({ id }) {
  const { formatMessage } = useIntl()
  const [lastRefresh, setLastRefresh] = useState()
  const [lastRefreshText, setLastRefreshText] = useState()

  useEffect(() => {
    if (!lastRefresh) return
    const token = setInterval(() => {
      const newAgo = timeAgo(lastRefresh)
      if (newAgo !== lastRefreshText) setLastRefreshText(newAgo)
    }, 1000)
    return () => {
      clearInterval(token)
    }
  }, [lastRefresh, lastRefreshText])

  const metricsQuery = useQuery({
    queryKey: ['message', id, 'metrics'],
    queryFn: () =>
      effects.apis.message.metric
        .count({
          messageId: id,
        })
        .then((data) => {
          const lastRefresh = new Date()
          setLastRefresh(lastRefresh)
          setLastRefreshText(timeAgo(lastRefresh))
          return metricsCountSchema.parse(data)
        }),
  })

  const [showRecipientListByMetricType, setShowRecipientListByMetricType] =
    useState()

  if (!metricsQuery.data || !lastRefresh) {
    return (
      <div className="w-full flex items-center justify-center h-[calc(100svh-99px)]">
        <CircularProgress />
      </div>
    )
  }

  if (showRecipientListByMetricType) {
    return (
      <NewMetricsRecipientList
        id={id}
        metricsType={showRecipientListByMetricType}
        onDismiss={() => {
          setShowRecipientListByMetricType(undefined)
        }}
      />
    )
  }

  const deliveryProgressPercentage = Math.round(
    ((metricsQuery.data.deliveredCount + metricsQuery.data.notDeliveredCount) /
      metricsQuery.data.totalCount) *
      100,
  )
  const hasInProgress = deliveryProgressPercentage !== 100

  return (
    <div className="space-y-12">
      <div className="bg-primary-100 rounded-lg overflow-hidden">
        <div className="relative flex items-center justify-center">
          <MetricsHeaderBackground className="min-h-[38px] w-full" />
          <div className="absolute text-16 text-primary-400">
            {formatMessage({
              id: 'text.metrics',
            })}
          </div>
        </div>

        <div className="flex flex-col p-12 space-y-20">
          <div className="flex text-primary-400 justify-between items-center">
            <span className="text-14">
              <FormattedMessage id="text.total.recipients" />
            </span>

            <span className="text-14 text-primary-300">
              <FormattedMessage
                id="text.last.updated"
                values={{ lastRefreshText }}
              />
            </span>
          </div>

          <div className="flex text-primary-400 justify-between items-center">
            <span className="text-20 font-medium">
              {metricsQuery.data.totalCount.toLocaleString()}
            </span>

            <button
              type="button"
              className="space-x-2 flex items-center"
              disabled={metricsQuery.isFetching}
              onClick={() => {
                metricsQuery.refetch()
              }}
            >
              <Refresh className="size-14 shrink-0" />
              <span
                className={clsx(
                  'text-12 underline',
                  metricsQuery.isFetching && 'text-primary-300',
                )}
              >
                {metricsQuery.isFetching
                  ? formatMessage({
                      id: 'metrics.refreshing',
                    })
                  : formatMessage({
                      id: 'metrics.refresh.data',
                    })}
              </span>
            </button>
          </div>

          {hasInProgress && (
            <div className="flex flex-col text-primary-400 space-y-18">
              <div className="w-full bg-primary-200 h-12 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-100"
                  style={{
                    width: `${deliveryProgressPercentage}%`,
                  }}
                ></div>
              </div>

              <div className="text-12 text-primary-300">
                <FormattedMessage
                  id="delivery.progress.hint"
                  values={{
                    status: (
                      <span className="text-primary-400">
                        <FormattedMessage id="text.inProgress" />
                      </span>
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">
        <div className="text-14 text-primary-400">
          <FormattedMessage id="text.delivery.status" />
        </div>
        <div
          className={clsx(
            'gap-16 grid',
            hasInProgress ? 'grid-cols-3' : 'grid-cols-2',
          )}
        >
          <MetricItem
            label={formatMessage({
              id: 'metrics.delivered',
            })}
            value={metricsQuery.data.deliveredCount}
            showInfo={false}
            onDetailClick={() => {
              setShowRecipientListByMetricType('delivered')
            }}
          />
          <MetricItem
            label={formatMessage({
              id: 'metrics.not.delivered',
            })}
            value={metricsQuery.data.notDeliveredCount}
            showInfo={false}
            onDetailClick={() => {
              setShowRecipientListByMetricType('not-delivered')
            }}
          />
          {hasInProgress && (
            <MetricItem
              label={formatMessage({
                id: 'metrics.delivering',
              })}
              value={metricsQuery.data.inProgressCount}
              showInfo={false}
              onDetailClick={() => {
                setShowRecipientListByMetricType('in-progress')
              }}
            />
          )}
        </div>
      </div>

      <div className="space-y-8">
        <div className="text-14 text-primary-400">
          <FormattedMessage id="text.interactions" />
        </div>
        <div className="space-y-12 border rounded-8 p-12 border-primary-350">
          <div className="flex items-center space-x-2 justify-between">
            <div className="flex space-y-8 flex-col">
              <span className="text-14 text-primary-400">
                <FormattedMessage id="text.total.interactions" />
              </span>
              <span className="text-18 text-primary-400 font-medium">
                {(
                  metricsQuery.data.readCount +
                  metricsQuery.data.likedCount +
                  metricsQuery.data.listenedCount +
                  metricsQuery.data.reportedCount
                ).toLocaleString()}
              </span>
            </div>

            <ListenFeedbackIcon className="size-52" />
          </div>

          <div className="gap-16 grid grid-cols-2">
            <MetricItem
              label={formatMessage({
                id: 'metrics.message.views',
              })}
              value={metricsQuery.data.readCount}
              showInfo={false}
              onDetailClick={() => {
                setShowRecipientListByMetricType('viewed')
              }}
            />
            <MetricItem
              label={formatMessage({
                id: 'metrics.reaction',
              })}
              value={metricsQuery.data.likedCount}
              showInfo={false}
              onDetailClick={() => {
                setShowRecipientListByMetricType('liked')
              }}
            />
            <MetricItem
              label={formatMessage({
                id: 'metrics.listen.text.to.speech',
              })}
              value={metricsQuery.data.listenedCount}
              showInfo={false}
              onDetailClick={() => {
                setShowRecipientListByMetricType('listened')
              }}
            />
            <MetricItem
              label={formatMessage({
                id: 'metrics.report',
              })}
              value={metricsQuery.data.reportedCount}
              showInfo={false}
              onDetailClick={() => {
                setShowRecipientListByMetricType('reported')
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function MetricItem({
  label,
  value,
  showInfo,
  onShowInfoClick,
  onDetailClick,
}) {
  const valueNotNull = value != null && value > 0

  return (
    <div
      className={clsx(
        'space-y-12 border rounded-8 p-12 border-primary-350',
        valueNotNull &&
          'cursor-pointer hover:bg-primary-100 hover:border-secondary',
      )}
      onClick={() => {
        if (valueNotNull) {
          onDetailClick()
        }
      }}
    >
      <div className="flex items-center space-x-2">
        <span className="text-14 text-primary-400">{label}</span>
        {showInfo && (
          <button
            type="button"
            className="size-16 shrink-0"
            onClick={onShowInfoClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                fill="#8D8D8D"
                d="M7.505 11.1h.95V7.3h-.95zm.428-4.972a.51.51 0 0 0 .372-.146q.15-.146.15-.36a.53.53 0 0 0-.15-.384.5.5 0 0 0-.372-.155.5.5 0 0 0-.372.155.53.53 0 0 0-.15.383q0 .216.15.361.15.146.372.146m.004 8.138a6.1 6.1 0 0 1-2.462-.498 6.4 6.4 0 0 1-2.015-1.362 6.4 6.4 0 0 1-1.362-2.016A6.1 6.1 0 0 1 1.6 7.925q0-1.311.498-2.465A6.3 6.3 0 0 1 3.46 3.452a6.5 6.5 0 0 1 2.016-1.354A6.1 6.1 0 0 1 7.941 1.6q1.311 0 2.465.498a6.4 6.4 0 0 1 2.008 1.354q.855.855 1.354 2.01.498 1.157.498 2.467a6.1 6.1 0 0 1-.498 2.462 6.5 6.5 0 0 1-1.354 2.012 6.4 6.4 0 0 1-2.011 1.362 6.1 6.1 0 0 1-2.466.501m.004-.95q2.24 0 3.808-1.575t1.567-3.816q0-2.24-1.564-3.808T7.932 2.55q-2.231 0-3.807 1.564Q2.55 5.678 2.55 7.933q0 2.232 1.575 3.808t3.816 1.575"
              ></path>
            </svg>
          </button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-18 text-primary-400">
          {valueNotNull ? value.toLocaleString() : '-'}
        </span>

        {valueNotNull && (
          <button
            type="button"
            className="size-20 shrink-0"
            onClick={onDetailClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 21 20"
            >
              <path
                fill="#5D7290"
                d="M18.834 10c0-4.6-3.734-8.334-8.334-8.334a8.336 8.336 0 0 0-8.333 8.333c0 4.6 3.733 8.334 8.333 8.334s8.334-3.734 8.334-8.334Zm-15 0A6.665 6.665 0 0 1 10.5 3.332a6.665 6.665 0 0 1 6.667 6.666 6.665 6.665 0 0 1-6.667 6.667 6.665 6.665 0 0 1-6.666-6.667Zm10 0L10.5 13.332l-1.175-1.175 1.317-1.325H7.167V9.166h3.475L9.317 7.841 10.5 6.666z"
              ></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

const metricsCountSchema = z.object({
  deliveredCount: z.number(),
  notDeliveredCount: z.number(),
  inProgressCount: z.number(),
  likedCount: z.number(),
  listenedCount: z.number(),
  readCount: z.number(),
  reportedCount: z.number(),
  totalCount: z.number(),
})
